import { getContents, GetContentsParams } from '@/api';
import { ContentCard } from '@/entities/content';
import { useExtendFocusable } from '@/hooks/useExtendFocusable';
import { GoToTopButton } from '@/shared/ui';
import { Content } from '@/types';
import { isFocusScrollSuppressed, scrollElementTo } from '@/utils';
import { FocusContext } from '@noriginmedia/norigin-spatial-navigation';
import uniqBy from 'lodash/uniqBy';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type ContentListProps = {
  sourcePage: string;
  containerClassName?: string;
  headerClassName?: string;
  header?: React.ReactNode;
  params?: GetContentsParams;
  containerId?: number;
  topicId?: number;
};

export const ContentList = ({
  containerId,
  sourcePage,
  containerClassName,
  headerClassName,
  header,
  params,
  topicId,
}: ContentListProps) => {
  const scrollRef = useRef<null | HTMLDivElement>(null);

  const [loading, setLoading] = useState(false);
  const [isAllLoaded, setIsAllLoaded] = useState(false);
  const [contents, setContents] = useState<Array<Content>>([]);

  // ! focusable: false — 컨테이너 자신은 절대 포커스 대상이 되지 않게 한다. (isContainer 주석 참고)
  const { ref, focusKey } = useExtendFocusable({
    isContainer: true,
    saveLastFocusedChild: true,
    trackChildren: true,
    autoRestoreFocus: true,
    focusable: false,
  });
  const load = useCallback(
    async (offset: number, limit: number) => {
      setLoading(true);

      try {
        const { contents } = await getContents({
          ...params,
          offset,
          limit,
        });

        if (contents.length > 0) {
          setContents((prev) => uniqBy([...prev, ...contents], (content) => content.id));
        }
        if (contents.length < limit) {
          setIsAllLoaded(true);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    },
    [params],
  );

  const onExposed = useCallback(() => {
    load(contents.length, 30);
  }, [contents.length, load]);

  const onContentCardFocused = useCallback(
    ({ top }: { top: number }) => {
      if (isFocusScrollSuppressed('vertical')) {
        return;
      }

      const current = scrollRef.current;

      if (current) {
        scrollElementTo({ element: current, top: top - 128 });
      }
    },
    [scrollRef],
  );

  const components = useMemo(() => {
    return contents
      .filter((content) => content.type === 'VIDEO')
      .map((content, i) => {
        const focusKey = `content-${i}`;
        const isLast = i === contents.length - 1;

        return (
          <ContentCard
            sourcePage={sourcePage}
            tabIndex={i}
            key={content.id}
            content={content}
            focusKey={focusKey}
            onFocused={onContentCardFocused}
            onExposed={isLast && !loading && !isAllLoaded ? onExposed : undefined}
            weight={i}
            containerId={containerId}
            topicId={topicId}
          />
        );
      });
  }, [contents, sourcePage, containerId, onContentCardFocused, loading, isAllLoaded, onExposed, topicId]);

  const skeletonComponents = useMemo(
    () =>
      new Array(12)
        .fill(0)
        .map((_, i) => (
          <div
            key={i}
            className="w-[533px] h-[300px] mr-[20px] mb-[20px] relative overflow-hidden ease-in-out duration-300 rounded-[20px] outline-disable bg-gray-400 animate-pulse"
          />
        )),
    [],
  );

  useEffect(() => {
    load(0, 30);
  }, [load]);

  return (
    <main id="main-container" ref={scrollRef} className={containerClassName}>
      {/* Header */}
      <div className={headerClassName}>{header}</div>

      <div className="py-[50px] w-[1764px]">
        {/* Skeleton */}
        {components.length === 0 && loading && (
          <div className="mt-[61px] mb-[20px] flex flex-wrap items-center py-4 w-fit pl-[60px] pr-[40px]">
            {skeletonComponents}
          </div>
        )}

        {/* Contents */}
        {components.length > 0 && (
          <FocusContext.Provider value={focusKey}>
            <div ref={ref} className="mt-[61px] mb-[20px] flex flex-wrap items-center py-4 w-fit pl-[60px] pr-[40px]">
              {components}
              {loading && skeletonComponents}
            </div>

            {isAllLoaded && <GoToTopButton focusKey="content-0" scrollingRef={scrollRef} />}
          </FocusContext.Provider>
        )}
      </div>
    </main>
  );
};
